import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  FieldWrapper,
  FormPreloader,
  FormTitle,
  Label,
  RawButton,
  Text,
  useWatch,
  WarningIcon,
} from '@common-fe/common-fe';

import { useGetContactsByRoleIdQuery, useGetOrganizationByIdQuery } from '../../queries';
interface Props {
  isLoading?: boolean;
  roleId?: string;
  isEditMode?: boolean;
  organizationId?: string;
}

const MAX_DISPLAY_AMOUNT = 5;

const AffectedUsersCountSection: React.FC<Props> = ({
  organizationId,
  isLoading = false,
  roleId,
  isEditMode,
}) => {
  const watchOrganizationId = useWatch({
    name: 'organizationId',
  });
  const currentOrganizationId = useMemo(() => {
    return watchOrganizationId || organizationId;
  }, [organizationId, watchOrganizationId]);
  const { data: organizationData, isLoading: isOrganizationLoading } = useGetOrganizationByIdQuery({
    id: currentOrganizationId,
  });
  const { data, isLoading: isRolesLoading, total, isEmpty } = useGetContactsByRoleIdQuery({ id: roleId });
  const [expanded, setExpanded] = useState(false);
  const isShowToggle = useMemo(() => {
    if (!data) {
      return false;
    }
    return data.length > MAX_DISPLAY_AMOUNT;
  }, [data]);
  const currentList = useMemo(() => {
    const list = data || [];
    if (expanded) {
      return list;
    }
    return list.slice(0, MAX_DISPLAY_AMOUNT);
  }, [data, expanded]);

  const handleToggleExpand = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  return (
    <Box
      round="container1Round"
      data-testid="affected-users"
      background="canvas"
      pad="l"
      margin={{ top: 'l' }}
      border={{ size: 'small', color: 'border1' }}
    >
      <FormTitle>Users</FormTitle>
      {isLoading || isOrganizationLoading || isRolesLoading ? (
        <FormPreloader count={2} />
      ) : (
        <Box direction="column">
          {isEmpty ? (
            <FieldWrapper>
              {organizationData?.title && <Label data-testid="affected-users_empty_title">{organizationData?.title}</Label>}
              <Text data-testid="affected-users_empty_text">There are no registered users under this role.</Text>
            </FieldWrapper>
          ) : (
            currentList?.map((item) => (
              <FieldWrapper key={item.id} data-testid={`affected-users_${item.id}`}>
                <Label data-testid={`affected-users_${item.id}_title`}>{item.title}</Label>
                <Text data-testid={`affected-users_${item.id}_count`}>{item.count}</Text>
              </FieldWrapper>
            ))
          )}
          {isShowToggle && (
            <RawButton data-testid="affected-users_expand_button" onClick={handleToggleExpand}>
              <Text color="textAccent" weight={400}>
                {expanded ? 'Collapse all' : `Show All (${data?.length})`}
              </Text>
            </RawButton>
          )}
        </Box>
      )}

      {isEditMode && !!total && (
        <Box
          round="container1Round"
          margin={{ top: 'l' }}
          background="warningContainer"
          border={{ size: 'small', color: 'warningBorder' }}
          pad={{ horizontal: 'l', vertical: 'm' }}
          style={{ position: 'relative' }}
        >
          <Box
            margin={{ horizontal: 'l' }}
            background="warning"
            height="2px"
            style={{ position: 'absolute', top: -1, left: 0, right: 0 }}
          >
          </Box>
          <Box direction="row" data-testid="affected-users_warning">
            <WarningIcon />
            <Text data-testid="affected-users_warning_text" margin={{ left: 'xs' }}>Editing this role will impact {total} users.</Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AffectedUsersCountSection;
